import type { User } from '@sentry/angular';
import { ProfileWebModel } from '@data-access';

export interface SentryAccount {
  user: User | null;
  /** Every key is always present: `undefined` drops a tag left over from the previous user. */
  tags: Record<'carrier_id' | 'customer_id' | 'admin_roles', string | undefined>;
  context: Record<string, unknown> | null;
}

/**
 * Who the error happened to. Contact and account fields only — no phone, address or birth date.
 * The first carrier/customer is the one the cabinet works with (`currentCarrierId`).
 */
export function toSentryAccount(profile: ProfileWebModel | undefined): SentryAccount {
  const carrier = profile?.access?.carriers?.[0];
  const customer = profile?.access?.customers?.[0];
  const adminRoles = profile?.access?.admin?.roles;
  const tags = {
    carrier_id: carrier?.carrierId,
    customer_id: customer?.customerId,
    admin_roles: adminRoles?.length ? adminRoles.join(',') : undefined,
  };

  if (!profile?.userId) return { user: null, tags, context: null };

  const { email, firstName, lastName } = profile.personal ?? {};
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return {
    user: { id: profile.userId, email, username: fullName || undefined },
    tags,
    context: {
      carrier: carrier && {
        name: carrier.carrierName,
        type: carrier.carrierType,
        status: carrier.carrierStatus,
        ownerType: carrier.carrierOwnerType,
        owner: carrier.owner,
      },
      customer: customer && {
        name: customer.customerName,
        type: customer.customerType,
        owner: customer.owner,
      },
      adminRoles,
    },
  };
}
